import React, { useRef, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react';

import { useDataStore } from 'state/DataStore';
import createSyncService from './createSyncService';
import { getSelectorForElement } from './selectorUtils';
import debounce from 'utilities/debounce';


const SyncContext = React.createContext(null);

export const SyncProvider = observer(({ children }) => {
    const dataStore = useDataStore();
    const syncServiceRef = useRef(null);

    useEffect(() => {
        const syncMode = dataStore.appSettings.syncMode;

        if (syncServiceRef.current) {
            syncServiceRef.current.disconnect();
            syncServiceRef.current = null;
        }

        if (syncMode === 'master' || syncMode === 'servant') {
            syncServiceRef.current = createSyncService(syncMode);
            syncServiceRef.current.connect();
        }

        return () => {
            if (syncServiceRef.current) {
                syncServiceRef.current.disconnect();
                syncServiceRef.current = null;
            }
        };
    }, [ dataStore.appSettings.syncMode ]);

    const sendSyncMessage = useCallback((msg) => {
        if (dataStore.appSettings.syncMode === 'master' && syncServiceRef.current) {
            syncServiceRef.current.send(msg);
        }
    }, [ dataStore ]);

    const value = { sendSyncMessage };

    return (
        <SyncContext.Provider value={value}>
            {children}
        </SyncContext.Provider>
    );
});

export const useSyncContext = () => React.useContext(SyncContext);

export function useSyncVideo(videoRef, isActive = true) {
    const syncContext = useSyncContext();
    const sendSyncMessage = syncContext?.sendSyncMessage;

    useEffect(() => {
        if (!isActive || !sendSyncMessage) return;

        const videoElement = videoRef?.current;
        if (!videoElement || videoElement.tagName !== 'VIDEO') return;

        const selector = getSelectorForElement(videoElement);
        if (!selector) return;

        const syncPlay = () => sendSyncMessage({ type: 'video', selector, action: 'play' });
        const syncPause = () => sendSyncMessage({ type: 'video', selector, action: 'pause' });
        const syncSeek = () => sendSyncMessage({ type: 'video', selector, action: 'seek', currentTime: videoElement.currentTime });
        const syncVolume = debounce(() => sendSyncMessage({ type: 'video', selector, action: 'volume', volume: videoElement.volume }), 150);
        const syncMuted = () => sendSyncMessage({ type: 'video', selector, action: 'muted', muted: videoElement.muted });
        const handleVolumeChange = () => { syncVolume(); syncMuted(); };

        videoElement.addEventListener('play', syncPlay);
        videoElement.addEventListener('pause', syncPause);
        videoElement.addEventListener('seeked', syncSeek);
        videoElement.addEventListener('volumechange', handleVolumeChange);

        return () => {
            syncVolume.cancel();
            videoElement.removeEventListener('play', syncPlay);
            videoElement.removeEventListener('pause', syncPause);
            videoElement.removeEventListener('seeked', syncSeek);
            videoElement.removeEventListener('volumechange', handleVolumeChange);
        };
    }, [ videoRef, isActive, sendSyncMessage ]);
}
